import { useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useCallback, useEffect, useState } from 'react'

import { setActiveProposal } from '@/lib/assistantAudit'
import { AssistantModel } from '@/http/models/assistant/Assistant'
import { AssistantProposal } from '@/types/assistant'

import { useAssistant } from './AssistantProvider'
import { useReviewStamp } from './useReviewStamp'

type StockProposal = Extract<AssistantProposal, { kind: 'stock_update' }>

/**
 * A stock change the assistant prepared for this product (?proposal=…): the page opens its Inventory tab with the
 * stock dialog prefilled, and the merchant's Save there goes through the normal stock endpoints.
 */
export const useStockProposal = (productId: string | undefined, onOpen: () => void) => {
  const [params, setParams] = useSearchParams()
  const proposalUid = params.get('proposal')
  const { markProposal } = useAssistant()
  const [proposal, setProposal] = useState<StockProposal | null>(null)
  const stamp = useReviewStamp()

  useEffect(() => {
    if (!proposalUid || !productId) return
    AssistantModel.getProposal(proposalUid).then((res) => {
      const p = res.success ? res.data?.result : null
      if (!p || p.kind !== 'stock_update') return
      if (p.target_id !== productId || p.status !== 'pending') {
        if (p.status !== 'pending') toast.info('This change was already handled.')
        setParams({}, { replace: true })
        return
      }
      setProposal(p)
      onOpen()
    })
    // onOpen is recreated each render; the proposal id (and a new Review click) decide when to open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalUid, productId, setParams, stamp])

  useEffect(() => {
    if (!proposal) return
    setActiveProposal(proposal.proposal_uid)
    return () => setActiveProposal(null)
  }, [proposal])

  const finish = useCallback((status: 'published' | 'discarded') => {
    if (!proposal) return
    markProposal(proposal.proposal_uid, status)
    setProposal(null)
    setParams({}, { replace: true })
  }, [markProposal, proposal, setParams])

  return {
    proposal,
    /** Changes on each Review click, to reopen the dialog. */
    stamp,
    onSaved: () => finish('published'),
    discard: () => finish('discarded'),
  }
}
